<template>
  <div class="row">
    <div class="col-md-12">
      <div class="card">
        <div class="card-header">
          <h4 class="card-title">Head Hunting</h4>
          <div class="mt-2">
            <ul class="nav nav-tabs card-header-tabs">
              <li class="nav-item">
                <a
                  class="nav-link"
                  :class="{ active: activeTab === 'candidates' }"
                  href="#"
                  @click.prevent="activeTab = 'candidates'"
                >Candidates</a>
              </li>
              <li class="nav-item">
                <a
                  class="nav-link"
                  :class="{ active: activeTab === 'scoring' }"
                  href="#"
                  @click.prevent="activeTab = 'scoring'"
                >Failed Scoring</a>
              </li>
              <li class="nav-item">
                <a
                  class="nav-link"
                  :class="{ active: activeTab === 'partial' }"
                  href="#"
                  @click.prevent="activeTab = 'partial'"
                >Partial Extraction</a>
              </li>
            </ul>
          </div>
        </div>
        <div class="card-body">
          <!-- Scoring Queue Tab -->
          <ScoringQueue v-if="activeTab === 'scoring'" mode="failed" key="scoring-failed" />

          <!-- Partial Extraction Tab -->
          <ScoringQueue v-if="activeTab === 'partial'" mode="partial" key="scoring-partial" />

          <!-- Search and Filters -->
          <template v-if="activeTab === 'candidates'">
          <div class="row mb-1">
            <div class="col-md-2">
              <label class="small text-muted mb-0">Candidate name</label>
              <BFormInput
                v-model="filters.name"
                placeholder="Name or email..."
                size="sm"
                @input="debouncedSearch"
              />
            </div>
            <div class="col-md-4">
              <label class="small text-muted mb-0">Resume content</label>
              <BFormInput
                v-model="filters.search"
                placeholder="Search resume..."
                size="sm"
                @input="debouncedSearch"
              />
            </div>
            <div class="col-md-2">
              <label class="small text-muted mb-0">Job title</label>
              <BFormInput
                v-model="filters.job_title"
                placeholder="Job title..."
                size="sm"
                @input="debouncedSearch"
              />
            </div>
            <div class="col-md-2">
              <label class="small text-muted mb-0">City</label>
              <BFormInput
                v-model="filters.city"
                placeholder="City..."
                size="sm"
                @input="debouncedSearch"
              />
            </div>
            <div class="col-md-1">
              <label class="small text-muted mb-0">Province</label>
              <BFormInput
                v-model="filters.state"
                placeholder="Prov..."
                size="sm"
                @input="debouncedSearch"
              />
            </div>
            <div class="col-md-1">
              <label class="small text-muted mb-0">Country</label>
              <BFormInput
                v-model="filters.country"
                placeholder="CA..."
                size="sm"
                @input="debouncedSearch"
              />
            </div>
          </div>
          <div class="row mb-2">
            <div class="col-md-1">
              <label class="small text-muted mb-0">Min Fit</label>
              <BFormInput
                v-model.number="filters.min_overall_fit"
                type="number"
                min="0"
                max="100"
                placeholder="0"
                size="sm"
                @input="debouncedSearch"
              />
            </div>
            <div class="col-md-1">
              <label class="small text-muted mb-0">Min Skills</label>
              <BFormInput
                v-model.number="filters.min_skills_match"
                type="number"
                min="0"
                max="100"
                placeholder="0"
                size="sm"
                @input="debouncedSearch"
              />
            </div>
            <div class="col-md-1">
              <label class="small text-muted mb-0">Min Loc.</label>
              <BFormInput
                v-model.number="filters.min_location_proximity"
                type="number"
                min="0"
                max="100"
                placeholder="0"
                size="sm"
                @input="debouncedSearch"
              />
            </div>
            <div class="col-md-1">
              <label class="small text-muted mb-0">Min Exp. (y)</label>
              <BFormInput
                v-model.number="filters.min_experience_years"
                type="number"
                min="0"
                placeholder="0"
                size="sm"
                @input="debouncedSearch"
              />
            </div>
          </div>

          <!-- Results info + clear -->
          <div class="mb-3 d-flex align-items-center">
            <small class="text-muted">
              <span v-if="!isTableBusy">{{ total.toLocaleString() }} candidates found</span>
              <span v-else>Loading...</span>
            </small>
            <BButton v-if="hasFilters" variant="link" size="sm" class="ml-2 p-0" @click="clearFilters">
              <i class="fa fa-times"></i> Clear filters
            </BButton>
          </div>

          <!-- Table -->
          <div class="table-responsive">
            <BTable
              :items="candidates"
              :fields="fields"
              :busy="isTableBusy"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              no-local-sorting
              @sort-changed="onSortChanged"
              hover
              show-empty
              empty-text="No candidates found"
            >
              <template #table-busy>
                <div class="text-center my-4">
                  <BSpinner class="align-middle"></BSpinner>
                  <span class="ml-2">Loading...</span>
                </div>
              </template>

              <template #cell(candidate)="{ item }">
                <router-link
                  :to="{ name: 'head-hunting-candidate', params: { candidateProfileId: item.id } }"
                  class="font-weight-medium"
                >
                  {{ item.candidate.full_name }}
                </router-link>
                <BBadge v-if="item.candidate.deleted_at" variant="danger" class="ml-1">DELETED</BBadge>
                <div class="small text-muted">{{ item.candidate.email }}</div>
              </template>

              <template #cell(city)="{ item }">
                <span v-if="item.location.city">{{ item.location.city }}</span>
                <span v-else class="text-muted">-</span>
              </template>

              <template #cell(state)="{ item }">
                <span v-if="item.location.state">{{ item.location.state }}</span>
                <span v-else class="text-muted">-</span>
              </template>

              <template #cell(country)="{ item }">
                <span v-if="item.location.country">{{ item.location.country }}</span>
                <span v-else class="text-muted">-</span>
              </template>

              <template #cell(ip_country)="{ item }">
                <span v-if="item.candidate.ip_country" :title="item.candidate.ip_country">{{ countryFlag(item.candidate.ip_country) }}</span>
                <span v-else class="text-muted">-</span>
              </template>

              <template #cell(overall_fit)="{ item }">
                <span v-if="item.ai_scores && item.ai_scores.overall_fit !== null">
                  {{ item.ai_scores.overall_fit }}
                </span>
                <span v-else class="text-muted">-</span>
              </template>

              <template #cell(skills_match)="{ item }">
                <span v-if="item.ai_scores && item.ai_scores.skills_match !== null">
                  {{ item.ai_scores.skills_match }}
                </span>
                <span v-else class="text-muted">-</span>
              </template>

              <template #cell(location_proximity)="{ item }">
                <span v-if="item.ai_scores && item.ai_scores.location_proximity !== null">
                  {{ item.ai_scores.location_proximity }}
                </span>
                <span v-else class="text-muted">-</span>
              </template>

              <template #cell(experience)="{ item }">
                <span v-if="item.ai_scores && item.ai_scores.experience_level">
                  {{ capitalizeFirst(item.ai_scores.experience_level) }}
                  <span v-if="item.ai_scores.experience_years" class="text-muted">
                    ({{ item.ai_scores.experience_years }}y)
                  </span>
                </span>
                <span v-else class="text-muted">-</span>
              </template>

              <template #cell(applications_count)="{ item }">
                <span
                  v-if="item.recent_applications && item.recent_applications.length"
                  :id="`popover-apps-${item.candidate.id}`"
                  class="applications-count"
                >
                  {{ item.applications_count }}
                </span>
                <span v-else>{{ item.applications_count }}</span>
                <BPopover
                  v-if="item.recent_applications && item.recent_applications.length"
                  :target="`popover-apps-${item.candidate.id}`"
                  triggers="hover"
                  placement="top"
                >
                  <template #title>Recent Applications</template>
                  <ul class="mb-0 pl-3">
                    <li v-for="(title, idx) in item.recent_applications" :key="idx" class="small">
                      {{ title }}
                    </li>
                  </ul>
                </BPopover>
              </template>

              <template #cell(actions)="{ item }">
                <div class="btn-group">
                  <BButton
                    size="sm"
                    variant="outline-info"
                    @click="previewResume(item)"
                    v-if="item.document_cdn"
                    title="Preview CV"
                  >
                    <i class="fa fa-eye"></i>
                  </BButton>
                  <BButton
                    size="sm"
                    variant="outline-primary"
                    :href="item.document_cdn"
                    target="_blank"
                    v-if="item.document_cdn"
                    title="Download CV"
                  >
                    <i class="fa fa-download"></i>
                  </BButton>
                  <router-link
                    :to="{ name: 'head-hunting-candidate', params: { candidateProfileId: item.id } }"
                    class="btn btn-sm btn-outline-secondary"
                    title="View Profile"
                  >
                    <i class="fa fa-user"></i>
                  </router-link>
                </div>
              </template>
            </BTable>

            <BPagination
              v-if="total > perPage"
              :disabled="isTableBusy"
              v-model="currentPage"
              @input="fetchCandidates"
              :total-rows="total"
              :per-page="perPage"
              align="center"
            ></BPagination>
          </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Resume Preview Modal -->
    <BModal
      v-model="showPreviewModal"
      :title="previewCandidate ? previewCandidate.candidate.full_name + ' - Resume' : 'Resume Preview'"
      size="xl"
      hide-footer
      body-class="p-0"
      @hidden="onPreviewHidden"
    >
      <div v-if="previewCandidate" class="resume-preview-container">
        <iframe
          v-if="isPdf(previewCandidate.document_cdn)"
          :src="previewCandidate.document_cdn"
          class="resume-iframe"
        ></iframe>
        <div
          v-else-if="isPreviewDocx && docxState !== 'error'"
          ref="docxHost"
          class="resume-iframe docx-frame"
        >
          <p v-if="docxState === 'loading'" class="p-4 text-center">Loading preview…</p>
        </div>
        <iframe
          v-else-if="isPreviewDoc && docPdfState === 'ready'"
          :src="docPdfUrl"
          title="Resume preview"
          class="resume-iframe"
        ></iframe>
        <div
          v-else-if="isPreviewDoc && docPdfState === 'loading'"
          class="resume-iframe docx-frame"
        >
          <p class="p-4 text-center" role="status">Loading preview…</p>
        </div>
        <div v-else class="p-4 text-center">
          <p class="mb-3">This document type cannot be previewed directly.</p>
          <BButton :href="previewCandidate.document_cdn" target="_blank" variant="primary">
            <i class="fa fa-download"></i> Download to view
          </BButton>
        </div>
      </div>
    </BModal>
  </div>
</template>

<script>
import {
  BTable,
  BSpinner,
  BPagination,
  BFormInput,
  BBadge,
  BButton,
  BModal,
  BPopover
} from "bootstrap-vue";
import debounce from "lodash/debounce";
import ScoringQueue from "./ScoringQueue.vue";
import {
  isDocx,
  isDoc,
  fetchDocumentBytes,
  fetchDocPreviewUrl,
  revokeDocPreviewUrl,
  renderDocxInto,
  clearDocx
} from "@/utils/docx-preview.js";

export default {
  components: {
    BTable,
    BSpinner,
    BPagination,
    BFormInput,
    BBadge,
    BButton,
    BModal,
    BPopover,
    ScoringQueue
  },
  data() {
    return {
      activeTab: "candidates",
      filters: {
        name: "",
        search: "",
        job_title: "",
        city: "",
        state: "",
        country: "",
        min_overall_fit: null,
        min_skills_match: null,
        min_location_proximity: null,
        min_experience_years: null
      },
      candidates: [],
      currentPage: 1,
      perPage: 15,
      total: 0,
      isTableBusy: true,
      sortBy: null,
      sortDesc: true,
      showPreviewModal: false,
      previewCandidate: null,
      docxState: "idle", // idle | loading | rendered | error
      docxToken: 0,
      docPdfState: "idle", // idle | loading | ready | error
      docPdfUrl: null,
      fields: [
        { key: "candidate", label: "Candidate" },
        { key: "city", label: "City", thStyle: { width: "120px" } },
        { key: "state", label: "Province", thStyle: { width: "100px" } },
        { key: "country", label: "Country", thStyle: { width: "80px" } },
        { key: "ip_country", label: "IP", thStyle: { width: "50px" }, class: "text-center" },
        { key: "overall_fit", label: "Fit", sortable: true, thStyle: { width: "60px" }, class: "text-center" },
        { key: "skills_match", label: "Skills", sortable: true, thStyle: { width: "60px" }, class: "text-center" },
        { key: "location_proximity", label: "Loc.", sortable: true, thStyle: { width: "60px" }, class: "text-center", thAttr: { title: "Location Proximity" } },
        { key: "experience", label: "Experience", sortable: true, thStyle: { width: "120px" } },
        { key: "applications_count", label: "Apps", sortable: true, thStyle: { width: "60px" }, class: "text-center" },
        { key: "actions", label: "", thStyle: { width: "120px" }, class: "text-right" }
      ]
    };
  },
  computed: {
    isPreviewDocx() {
      return !!(
        this.previewCandidate &&
        this.previewCandidate.document_id &&
        isDocx(this.previewCandidate.document_cdn)
      );
    },
    isPreviewDoc() {
      return !!(
        this.previewCandidate &&
        this.previewCandidate.document_id &&
        isDoc(this.previewCandidate.document_cdn)
      );
    },
    hasFilters() {
      return !!(
        this.filters.name ||
        this.filters.search ||
        this.filters.job_title ||
        this.filters.city ||
        this.filters.state ||
        this.filters.country ||
        this.filters.min_overall_fit ||
        this.filters.min_skills_match ||
        this.filters.min_location_proximity ||
        this.filters.min_experience_years
      );
    }
  },
  created() {
    this.debouncedSearch = debounce(this.applyFilters, 300);
    this.fetchCandidates();
  },
  beforeDestroy() {
    // Leaving the page with the modal open: release the .doc PDF.
    this.docxToken++;
    revokeDocPreviewUrl(this.docPdfUrl);
  },
  methods: {
    applyFilters() {
      this.$nextTick(() => {
        this.currentPage = 1;
        this.fetchCandidates();
      });
    },
    fetchCandidates() {
      this.isTableBusy = true;
      this.$http
        .get("/enroll_admin/head-hunting/candidates", {
          params: {
            page: this.currentPage,
            per_page: this.perPage,
            "filters[name]": this.filters.name || undefined,
            "filters[search]": this.filters.search || undefined,
            "filters[job_title]": this.filters.job_title || undefined,
            "filters[city]": this.filters.city || undefined,
            "filters[state]": this.filters.state || undefined,
            "filters[country]": this.filters.country || undefined,
            "filters[min_overall_fit]": this.filters.min_overall_fit || undefined,
            "filters[min_skills_match]": this.filters.min_skills_match || undefined,
            "filters[min_location_proximity]": this.filters.min_location_proximity || undefined,
            "filters[min_experience_years]": this.filters.min_experience_years || undefined,
            sort_by: this.sortBy || undefined,
            sort_desc: this.sortDesc ? 1 : 0
          }
        })
        .then(({ data }) => {
          this.currentPage = data.current_page;
          this.total = data.total;
          this.candidates = data.data || [];
        })
        .catch(() => {
          this.candidates = [];
        })
        .finally(() => {
          this.isTableBusy = false;
        });
    },
    clearFilters() {
      this.filters = {
        name: "",
        search: "",
        job_title: "",
        city: "",
        state: "",
        country: "",
        min_overall_fit: null,
        min_skills_match: null,
        min_location_proximity: null,
        min_experience_years: null
      };
      this.currentPage = 1;
      this.fetchCandidates();
    },
    onSortChanged(ctx) {
      this.sortBy = ctx.sortBy;
      this.sortDesc = ctx.sortDesc;
      this.currentPage = 1;
      this.fetchCandidates();
    },
    getScoreVariant(score) {
      if (score === null || score === undefined) return "secondary";
      if (score >= 75) return "success";
      if (score >= 60) return "info";
      if (score >= 45) return "warning";
      return "danger";
    },
    countryFlag(code) {
      if (!code || code.length !== 2) return code;
      return String.fromCodePoint(...[...code.toUpperCase()].map(c => 0x1F1E6 + c.charCodeAt(0) - 65));
    },
    capitalizeFirst(str) {
      if (!str) return "";
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
    previewResume(item) {
      this.previewCandidate = item;
      this.showPreviewModal = true;
      if (item.document_id && isDocx(item.document_cdn)) {
        this.loadDocx(item.document_id);
      } else if (item.document_id && isDoc(item.document_cdn)) {
        this.loadDocPdf(item.document_id);
      }
    },
    loadDocPdf(documentId) {
      const token = ++this.docxToken;
      this.releaseDocPdf();
      this.docPdfState = "loading";
      fetchDocPreviewUrl(this.$http, documentId)
        .then(url => {
          // Closed or switched meanwhile: this URL is nobody's.
          if (token !== this.docxToken) return revokeDocPreviewUrl(url);
          this.docPdfUrl = url;
          this.docPdfState = "ready";
        })
        .catch(() => {
          if (token === this.docxToken) this.docPdfState = "error";
        });
    },
    releaseDocPdf() {
      revokeDocPreviewUrl(this.docPdfUrl);
      this.docPdfUrl = null;
      this.docPdfState = "idle";
    },
    loadDocx(documentId) {
      const token = ++this.docxToken;
      this.docxState = "loading";
      fetchDocumentBytes(this.$http, documentId)
        .then(({ data }) => this.$nextTick().then(() => ({ data })))
        .then(async ({ data }) => {
          if (token !== this.docxToken || !this.$refs.docxHost) return;
          // Kept outside $refs: BModal removes its content before @hidden fires.
          this.docxRenderedHost = this.$refs.docxHost;
          await renderDocxInto(this.$refs.docxHost, data);
          if (token === this.docxToken) this.docxState = "rendered";
        })
        .catch(() => {
          if (token === this.docxToken) this.docxState = "error";
        });
    },
    onPreviewHidden() {
      // Invalidate any in-flight load; the modal content unmounts with previewCandidate.
      this.docxToken++;
      clearDocx(this.docxRenderedHost);
      this.docxRenderedHost = null;
      this.docxState = "idle";
      this.releaseDocPdf();
      this.previewCandidate = null;
    },
    isPdf(url) {
      if (!url) return false;
      return url.toLowerCase().includes(".pdf");
    }
  }
};
</script>

<style scoped>
.card-category {
  color: #9a9a9a;
  font-size: 14px;
}

.font-weight-medium {
  font-weight: 500;
}

.resume-preview-container {
  min-height: 70vh;
}

.resume-iframe {
  width: 100%;
  height: 75vh;
  border: none;
}

.docx-frame {
  overflow: auto;
  background: #fff;
}

.applications-count {
  cursor: pointer;
  text-decoration: underline dotted;
}

.scores-cell {
  white-space: nowrap;
}
</style>
